import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Users, Inbox, Zap, MessageSquare, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useOverlay } from "../context/OverlayContext";
import VybeIcon from "./icons/VybeIcon";

/**
 * MobileBottomNav
 * - Fixed bottom nav
 * - Adds a spacer div to prevent page content scrolling underneath the nav
 * - Center Explore button contains custom Vybe Tribe V icon logo
 * - Center label is absolutely positioned (does NOT shrink the icon)
 * - FAB bottom-right for "Start a Vybe"
 */
export default function MobileBottomNav({
  unreadMessages = 0,
  onCreateVybe,
}: {
  unreadMessages?: number;
  onCreateVybe: () => void;
}) {
  const { pathname } = useLocation();
  const { isVerified } = useAuth();
  const { hasActiveOverlay } = useOverlay();
  const navigate = useNavigate();

  const isActive = (p: string) => pathname.startsWith(p);

  // Sizes for the center button and icon
  const BTN = 72; // circle size (px)

  // Reserve enough space so content never sits behind the fixed nav.
  // 96px is a safe “nav + label + touch room” height, plus safe-area inset.
  const NAV_SPACER_CLASS =
    "md:hidden h-[calc(env(safe-area-inset-bottom,0)+96px)]";

  const explorePath = "/vybes";
  const exploreTo = "/vybes?view=explore";
  const exploreActive = isActive(explorePath);

  return (
    <>
      {/* Spacer prevents content scrolling behind the fixed nav */}
      <div className={NAV_SPACER_CLASS} aria-hidden />

      {/* Bottom bar */}
      <nav
        className="
          bottom-nav
          fixed bottom-0 left-0 right-0 md:hidden
          z-40 bg-theme-cards/95 backdrop-blur
          border-t border-theme-cards
          px-3 pt-2 pb-[calc(env(safe-area-inset-bottom,0)+8px)]
        "
        role="navigation"
        aria-label="Primary"
      >
        <div className="grid grid-cols-5 items-center">
          {/* Tribe */}
          <Link
            to="/tribe"
            aria-label="Tribe"
            className={`flex items-center justify-center py-2 focus:outline-none focus:ring-0 ${
              isActive("/tribe") ? "active" : ""
            }`}
          >
            <Users className="w-6 h-6" />
          </Link>

          {/* My Vybe Requests */}
          <Link
            to="/myvyberequests"
            aria-label="My Vybe Requests"
            className={`flex items-center justify-center py-2 focus:outline-none focus:ring-0 ${
              isActive("/myvyberequests") ? "active" : ""
            }`}
          >
            <Inbox className="w-6 h-6" />
          </Link>

          {/* Center Explore (logo icon + on-brand gradient label) */}
          <button
            onClick={() => navigate(exploreTo)}
            aria-label="Explore Vybes"
            className="
              explore-btn
              mx-auto -translate-y-4
              flex items-center justify-center
              rounded-full relative z-50
              bg-transparent
              focus:outline-none focus:ring-0
              cursor-pointer
            "
            style={{ width: BTN, height: BTN }}
          >
            <VybeIcon
              size={BTN}
              strokeWidth={2}
              className="pointer-events-none"
            />

            <span
              className={`
                pointer-events-none
                absolute left-1/2 -translate-x-1/2
                -bottom-3
                text-[11px] font-semibold tracking-tight whitespace-nowrap
                bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-300
                bg-clip-text text-transparent
                drop-shadow-[0_1px_10px_rgba(168,85,247,0.35)]
                ${exploreActive ? "opacity-100" : "opacity-70"}
              `}
            >
              Explore Vybes
            </span>
          </button>

          {/* My Vybes */}
          <Link
            to="/myvybes"
            aria-label="My Vybes"
            className={`flex items-center justify-center py-2 focus:outline-none focus:ring-0 ${
              isActive("/myvybes") ? "active" : ""
            }`}
          >
            <Zap className="w-6 h-6" />
          </Link>

          {/* Messages */}
          <Link
            to="/messages"
            aria-label="Messages"
            className={`relative flex items-center justify-center py-2 focus:outline-none focus:ring-0 ${
              isActive("/messages") ? "active" : ""
            }`}
          >
            <MessageSquare className="w-6 h-6" />
            {unreadMessages > 0 && (
              <span className="absolute top-1 right-3 text-[10px] px-1.5 py-0.5 rounded-full bg-theme-primary text-white">
                {unreadMessages > 9 ? "9+" : unreadMessages}
              </span>
            )}
          </Link>
        </div>
      </nav>

      {/* Host Vybe (FAB) bottom-right - Hidden when modals/overlays are active */}
      {!hasActiveOverlay && (
        <button
          aria-label="Host Vybe"
          onClick={() => {
            if (!isVerified()) {
              navigate("/verify");
              return;
            }
            onCreateVybe();
          }}
          className="
            md:hidden fixed z-50
            right-4 bottom-[calc(env(safe-area-inset-bottom,0)+84px)]
            w-14 h-14 rounded-full
            bg-gradient-to-br from-rose-500 to-fuchsia-600
            shadow-[0_8px_24px_rgba(244,63,94,0.5)]
            hover:shadow-[0_12px_32px_rgba(244,63,94,0.6)]
            transition-all duration-200
            hover:scale-105 active:scale-95
          "
        >
          <Plus className="w-7 h-7 mx-auto my-auto text-white" />
        </button>
      )}
    </>
  );
}
